import { writable } from 'svelte/store';
import { getFromStorage, saveToStorage } from '../utils/localStorage';

export interface ThemeSettings {
  // Light mode colors (required)
  background: string;        // e.g., "#f8fafc" (slate-50)
  text: string;              // e.g., "#0f172a" (slate-900)
  accent: string;            // e.g., "#10b981" (green-500) - used for buttons and highlights
  backgroundGradientStart?: string;
  backgroundGradientEnd?: string;
  textGreen: string;          // e.g., "#16a34a" (green-600)
  
  // Dark mode colors (optional - fallback to Tailwind defaults if not set)
  backgroundDark?: string;        // e.g., "#0f172a" (slate-900)
  textDark?: string;              // e.g., "#f1f5f9" (slate-100)
  accentDark?: string;            // e.g., "#10b981" (green-500)
  backgroundGradientStartDark?: string;
  backgroundGradientEndDark?: string;
  textGreenDark?: string;          // e.g., "#34d399" (green-400)
  
  yellowThreshold: number;    // Cost threshold for yellow favicon/logo (default: 144)
  redThreshold: number;       // Cost threshold for red favicon/logo (default: 512)
}

export type PresetName = 'default' | 'ocean' | 'sunset' | 'forest';

const DEFAULT_SETTINGS: ThemeSettings = {
  background: '#f8fafc',      // slate-50
  text: '#0f172a',            // slate-900
  accent: '#10b981',          // green-500
  backgroundGradientStart: '#f8fafc',  // slate-50
  backgroundGradientEnd: '#f8fafc',    // slate-50
  textGreen: '#16a34a',       // green-600
  yellowThreshold: 144,      // Default threshold for yellow favicon/logo
  redThreshold: 512,          // Default threshold for red favicon/logo
};

/**
 * Color presets for quick theme switching
 */
export const COLOR_PRESETS: Record<PresetName, ThemeSettings> = {
  default: {
    background: '#f8fafc',      // slate-50
    text: '#0f172a',            // slate-900
    accent: '#10b981',          // green-500
    backgroundGradientStart: '#f8fafc',  // slate-50
    backgroundGradientEnd: '#e2e8f0',    // slate-200 (deutlicherer Gradient)
    textGreen: '#16a34a',       // green-600
    backgroundDark: '#0f172a',      // slate-900
    textDark: '#f1f5f9',            // slate-100
    accentDark: '#10b981',          // green-500
    backgroundGradientStartDark: '#0f172a',  // slate-900
    backgroundGradientEndDark: '#334155',    // slate-700 (deutlicherer Gradient)
    textGreenDark: '#34d399',       // green-400
    yellowThreshold: 144,
    redThreshold: 512,
  },
  ocean: {
    background: '#f0f9ff',      // sky-50
    text: '#0c4a6e',            // sky-900
    accent: '#06b6d4',          // cyan-500
    backgroundGradientStart: '#f0f9ff',  // sky-50
    backgroundGradientEnd: '#bae6fd',    // sky-300 (deutlicherer Gradient)
    textGreen: '#0891b2',       // cyan-600
    backgroundDark: '#082f49',      // sky-900
    textDark: '#e0f2fe',            // sky-100
    accentDark: '#06b6d4',          // cyan-500
    backgroundGradientStartDark: '#082f49',  // sky-900
    backgroundGradientEndDark: '#075985',    // sky-700 (deutlicherer Gradient)
    textGreenDark: '#22d3ee',       // cyan-400
    yellowThreshold: 144,
    redThreshold: 512,
  },
  sunset: {
    background: '#fff7ed',      // orange-50
    text: '#7c2d12',            // orange-900
    accent: '#f97316',          // orange-500
    backgroundGradientStart: '#fff7ed',  // orange-50
    backgroundGradientEnd: '#fed7aa',    // orange-200 (deutlicherer Gradient)
    textGreen: '#ea580c',       // orange-600
    backgroundDark: '#7c2d12',      // orange-900
    textDark: '#ffedd5',            // orange-100
    accentDark: '#f97316',          // orange-500
    backgroundGradientStartDark: '#7c2d12',  // orange-900
    backgroundGradientEndDark: '#c2410c',    // orange-700 (deutlicherer Gradient)
    textGreenDark: '#fb923c',       // orange-400
    yellowThreshold: 144,
    redThreshold: 512,
  },
  forest: {
    background: '#f0fdf4',      // green-50
    text: '#14532d',            // green-900
    accent: '#22c55e',          // green-500
    backgroundGradientStart: '#f0fdf4',  // green-50
    backgroundGradientEnd: '#bbf7d0',    // green-200 (deutlicherer Gradient)
    textGreen: '#16a34a',       // green-600
    backgroundDark: '#14532d',      // green-900
    textDark: '#dcfce7',            // green-100
    accentDark: '#22c55e',          // green-500
    backgroundGradientStartDark: '#14532d',  // green-900
    backgroundGradientEndDark: '#166534',    // green-800
    textGreenDark: '#4ade80',       // green-400
    yellowThreshold: 144,
    redThreshold: 512,
  },
};

/**
 * Check if theme settings exist in localStorage
 */
function hasStoredSettings(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  try {
    const item = window.localStorage.getItem('meeting-cost-counter.themeSettings');
    return item !== null;
  } catch {
    return false;
  }
}

/**
 * Initialize theme settings from localStorage or use first preset (default)
 */
function initThemeSettings(): ThemeSettings {
  // Check if settings already exist in localStorage
  const hasStored = hasStoredSettings();
  
  if (!hasStored) {
    // First time: use first preset (default)
    const firstPreset = COLOR_PRESETS.default;
    // Save it to localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(
          'meeting-cost-counter.themeSettings',
          JSON.stringify(firstPreset)
        );
      } catch (error) {
        console.warn('Failed to save initial preset to localStorage:', error);
      }
    }
    return firstPreset;
  }
  
  // Load existing settings
  const stored = getFromStorage<ThemeSettings>('themeSettings', DEFAULT_SETTINGS);
  
  // Ensure all required fields are present
  return {
    background: stored.background || DEFAULT_SETTINGS.background,
    text: stored.text || DEFAULT_SETTINGS.text,
    accent: stored.accent || DEFAULT_SETTINGS.accent,
    backgroundGradientStart: stored.backgroundGradientStart || DEFAULT_SETTINGS.backgroundGradientStart,
    backgroundGradientEnd: stored.backgroundGradientEnd || DEFAULT_SETTINGS.backgroundGradientEnd,
    textGreen: stored.textGreen || DEFAULT_SETTINGS.textGreen,
    // Dark mode colors (optional)
    backgroundDark: stored.backgroundDark,
    textDark: stored.textDark,
    accentDark: stored.accentDark,
    backgroundGradientStartDark: stored.backgroundGradientStartDark,
    backgroundGradientEndDark: stored.backgroundGradientEndDark,
    textGreenDark: stored.textGreenDark,
    yellowThreshold: stored.yellowThreshold ?? DEFAULT_SETTINGS.yellowThreshold,
    redThreshold: stored.redThreshold ?? DEFAULT_SETTINGS.redThreshold,
  };
}

/**
 * Apply custom colors to CSS variables based on current theme
 * This function should be called whenever colors change or theme switches
 */
function applyCustomColors(settings: ThemeSettings) {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  const isDark = root.classList.contains('dark');
  
  if (isDark) {
    // Apply dark mode colors - set them if available, otherwise keep CSS defaults
    // This ensures preset colors override defaults
    if (settings.backgroundDark) {
      root.style.setProperty('--color-bg-dark', settings.backgroundDark);
    }
    if (settings.textDark) {
      root.style.setProperty('--color-text-dark', settings.textDark);
    }
    if (settings.accentDark) {
      root.style.setProperty('--color-accent-dark', settings.accentDark);
    }
    // Always set gradient colors - use dark mode colors if available, otherwise fallback to light mode
    const darkGradientStart = settings.backgroundGradientStartDark || settings.backgroundDark || settings.backgroundGradientStart || settings.background;
    const darkGradientEnd = settings.backgroundGradientEndDark || settings.backgroundDark || settings.backgroundGradientEnd || settings.background;
    root.style.setProperty('--color-bg-gradient-start-dark', darkGradientStart);
    root.style.setProperty('--color-bg-gradient-end-dark', darkGradientEnd);
    if (settings.textGreenDark) {
      root.style.setProperty('--color-text-green-dark', settings.textGreenDark);
    }
  } else {
    // Apply light mode colors
    root.style.setProperty('--color-bg-light', settings.background);
    root.style.setProperty('--color-text-light', settings.text);
    root.style.setProperty('--color-accent-light', settings.accent);
    root.style.setProperty('--color-bg-gradient-start-light', settings.backgroundGradientStart || settings.background);
    root.style.setProperty('--color-bg-gradient-end-light', settings.backgroundGradientEnd || settings.background);
    root.style.setProperty('--color-text-green-light', settings.textGreen);
  }
}

/**
 * Re-apply custom colors with given settings (useful when theme changes)
 */
function reapplyCustomColors(settings: ThemeSettings) {
  if (typeof document === 'undefined') return;
  
  // Re-apply colors for current theme
  applyCustomColors(settings);
}

/**
 * Clear custom colors (restore defaults)
 */
function clearCustomColors() {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  // Clear light mode colors
  root.style.removeProperty('--color-bg-light');
  root.style.removeProperty('--color-text-light');
  root.style.removeProperty('--color-accent-light');
  root.style.removeProperty('--color-bg-gradient-start-light');
  root.style.removeProperty('--color-bg-gradient-end-light');
  root.style.removeProperty('--color-text-green-light');
  // Clear dark mode colors
  root.style.removeProperty('--color-bg-dark');
  root.style.removeProperty('--color-text-dark');
  root.style.removeProperty('--color-accent-dark');
  root.style.removeProperty('--color-bg-gradient-start-dark');
  root.style.removeProperty('--color-bg-gradient-end-dark');
  root.style.removeProperty('--color-text-green-dark');
}

/**
 * Theme settings store with localStorage persistence
 */
function createThemeSettingsStore() {
  const initialSettings = initThemeSettings();
  applyCustomColors(initialSettings);
  
  const { subscribe, set, update } = writable<ThemeSettings>(initialSettings);

  return {
    subscribe,
    
    /**
     * Get current custom colors
     */
    getCustomColors: (): ThemeSettings => {
      let current: ThemeSettings = DEFAULT_SETTINGS;
      subscribe((value) => {
        current = value;
      })();
      return current;
    },
    
    /**
     * Set a specific color value
     */
    setCustomColor: <K extends keyof ThemeSettings>(key: K, value: ThemeSettings[K]) => {
      update((settings) => {
        const updated = { ...settings, [key]: value };
        saveToStorage('themeSettings', updated);
        // Apply colors immediately
        applyCustomColors(updated);
        // Re-apply after a short delay to ensure DOM is updated
        setTimeout(() => {
          applyCustomColors(updated);
        }, 10);
        return updated;
      });
    },
    
    /**
     * Set yellow threshold
     */
    setYellowThreshold: (threshold: number) => {
      update((settings) => {
        const updated = { ...settings, yellowThreshold: Math.max(0, threshold) };
        saveToStorage('themeSettings', updated);
        return updated;
      });
    },
    
    /**
     * Set red threshold
     */
    setRedThreshold: (threshold: number) => {
      update((settings) => {
        const updated = { ...settings, redThreshold: Math.max(0, threshold) };
        saveToStorage('themeSettings', updated);
        return updated;
      });
    },
    
    /**
     * Set all theme settings at once
     */
    setThemeSettings: (settings: ThemeSettings) => {
      saveToStorage('themeSettings', settings);
      // Apply colors immediately
      applyCustomColors(settings);
      set(settings);
      // Re-apply after a short delay to ensure DOM is updated (especially for theme switches)
      setTimeout(() => {
        applyCustomColors(settings);
      }, 50);
    },
    
    /**
     * Re-apply custom colors (useful when theme changes)
     */
    reapplyColors: () => {
      let currentSettings: ThemeSettings = DEFAULT_SETTINGS;
      subscribe((value) => {
        currentSettings = value;
      })();
      reapplyCustomColors(currentSettings);
    },
    
    /**
     * Apply a color preset
     */
    applyPreset: (presetName: PresetName) => {
      const preset = COLOR_PRESETS[presetName];
      if (preset) {
        // Ensure gradient colors are always set (use background as fallback if not provided)
        const settingsWithGradient: ThemeSettings = {
          ...preset,
          backgroundGradientStart: preset.backgroundGradientStart || preset.background,
          backgroundGradientEnd: preset.backgroundGradientEnd || preset.background,
          // Dark mode gradients
          backgroundGradientStartDark: preset.backgroundGradientStartDark || preset.backgroundDark || preset.background,
          backgroundGradientEndDark: preset.backgroundGradientEndDark || preset.backgroundDark || preset.background,
        };
        setThemeSettings(settingsWithGradient);
      }
    },
    
    /**
     * Get all available preset names
     */
    getPresetNames: (): PresetName[] => {
      return Object.keys(COLOR_PRESETS) as PresetName[];
    },
    
    /**
     * Get a specific preset
     */
    getPreset: (presetName: PresetName): ThemeSettings | undefined => {
      return COLOR_PRESETS[presetName];
    },
    
    /**
     * Reset to default colors
     */
    resetToDefaults: () => {
      saveToStorage('themeSettings', DEFAULT_SETTINGS);
      applyCustomColors(DEFAULT_SETTINGS);
      set(DEFAULT_SETTINGS);
    },
    
    /**
     * Reset function for testing
     */
    reset: () => {
      clearCustomColors();
      set(DEFAULT_SETTINGS);
    },
  };
}

export const themeSettingsStore = createThemeSettingsStore();
